<template>
  <div class="pp-tab-surface">
    <div
      v-if="docs.length"
      class="pp-docs"
    >
      <a
        v-for="doc in docs"
        :key="`${doc.kind}-${doc.title}-${doc.src}`"
        :href="doc.src"
        target="_blank"
        rel="noopener noreferrer"
        class="pp-doc"
        @click="onDocClick(doc, $event)"
      >
        <div class="ic">
          <svg
            v-if="doc.kind !== 'video'"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            aria-hidden="true"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
          <svg
            v-else
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
        <div class="body">
          <div class="n">{{ doc.title }}</div>
          <div
            v-if="doc.meta"
            class="m"
          >
            {{ doc.meta }}
          </div>
        </div>
        <span
          class="dl"
          aria-hidden="true"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
        </span>
      </a>
    </div>
    <div
      v-else
      class="empty"
    >
      <p>{{ t('property.v2Documents.empty') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropertyDetailed } from '@/types/properties';
import { useAccessGate } from '@/composables/useAccessGate';

type DocItem = {
  kind: 'brochure' | 'floor_plan' | 'video';
  title: string;
  src: string;
  meta?: string;
};

const props = defineProps<{
  item: PropertyDetailed;
}>();

const { t } = useI18n();
const { gate } = useAccessGate();

const onDocClick = (doc: DocItem, event: MouseEvent) => {
  // Verified users get the actual link via the anchor's default behavior.
  // For guest/registered the gate intercepts and shows the right modal.
  const passed = gate(
    'document',
    () => {
      if (typeof window !== 'undefined') window.open(doc.src, '_blank', 'noopener,noreferrer');
    },
    { documentName: doc.title, documentMeta: doc.meta },
  );
  if (!passed) {
    event.preventDefault();
  }
};

type RawDoc = { name?: string; title?: string; url?: string; src?: string; type?: string; label?: string };

// Icon kind is coarse — only video vs. document matters visually.
const kindFor = (type?: string): DocItem['kind'] =>
  type === 'video' ? 'video' : type === 'floor_plan' ? 'floor_plan' : 'brochure';

const docs = computed<DocItem[]>(() => {
  // Preferred: the full, positively-typed `documents` list from the server —
  // each entry carries its real type + label, so every document shows with the
  // correct label instead of everything being mislabelled "Brochure".
  const documents = (props.item as { documents?: RawDoc[] }).documents;
  if (Array.isArray(documents) && documents.length) {
    return documents
      .map((d): DocItem | null => {
        const src = d.url || d.src || '';
        if (!src) return null;
        return {
          kind: kindFor(d.type),
          title: d.name || d.title || t('property.v2Documents.fallback.document'),
          src,
          meta: d.label || t('property.v2Documents.fallback.document'),
        };
      })
      .filter((d): d is DocItem => d != null);
  }

  // Fallback: legacy bucketed shape (brochures/floor_plans/videos) for callers
  // that don't pass the typed `documents` array.
  const out: DocItem[] = [];
  const brochures = (props.item as { brochures?: Array<{ src: string; title?: string }> }).brochures || [];
  brochures.forEach((b) => {
    out.push({
      kind: 'brochure',
      title: b.title || t('property.v2Documents.fallback.brochureTitle'),
      src: b.src,
      meta: t('property.v2Documents.fallback.brochureMeta'),
    });
  });
  const floorPlans = (props.item as { floor_plans?: Array<{ src: string; title?: string }> }).floor_plans || [];
  floorPlans.forEach((f) => {
    out.push({
      kind: 'floor_plan',
      title: f.title || t('property.v2Documents.fallback.floorPlan'),
      src: f.src,
      meta: t('property.v2Documents.fallback.floorPlan'),
    });
  });
  const videos = (props.item as { videos?: Array<{ src: string; title?: string }> }).videos || [];
  videos.forEach((v) => {
    out.push({
      kind: 'video',
      title: v.title || t('property.v2Documents.fallback.videoTitle'),
      src: v.src,
      meta: t('property.v2Documents.fallback.videoMeta'),
    });
  });
  return out;
});
</script>

<style scoped lang="scss">
.pp-tab-surface {
  background: #f8f7f3;
  border-radius: 20px;
  padding: 28px;
}

.pp-docs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (max-width: 744px) {
    grid-template-columns: 1fr;
  }
}

.pp-doc {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: background 0.15s ease;

  &:hover {
    background: #f8f7f3;
  }

  .ic {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: #fff6e7;
    color: #8b6000;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .body {
    flex: 1;
    min-width: 0;

    .n {
      font-size: 13.5px;
      font-weight: 700;
      color: #0d0f15;
    }

    .m {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10.5px;
      color: rgba(13, 15, 21, 0.62);
      margin-top: 2px;
      letter-spacing: 0.02em;
    }
  }

  .dl {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid rgba(13, 15, 21, 0.08);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(13, 15, 21, 0.7);
    flex-shrink: 0;
  }
}

.empty {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 14px;
  padding: 40px;
  text-align: center;
  color: rgba(13, 15, 21, 0.62);
  font-size: 14px;
}
</style>
